<template>
    <div class="sbs-video-player">
        <template v-if="player">
            <!-- @slot Слот для спиннера загрузки/буферизации -->
            <slot name="loadingSpinner">
                <SbsVideoPlayerLoadingSpinner :colorTheme="colorTheme" />
            </slot>

            <!-- @slot Слот для кнопки переключения между паузой и воспроизведением -->
            <slot name="playToggle">
                <SbsVideoPlayerPlayToggle />
            </slot>

            <!-- @slot Слот для кнопки управления скорости видео -->
            <slot name="playbackRate">
                <SbsVideoPlayerPlaybackRate />
            </slot>

            <!-- @slot Слот для строки прогресса воспроизведения и загрузки видео -->
            <slot name="progressControl">
                <SbsVideoPlayerProgressControl />
            </slot>

            <!-- @slot Слот для показа текущего времени и продолжительности видео -->
            <slot name="currentTime">
                <SbsVideoPlayerTimeDisplay />
            </slot>

            <!-- @slot Слот для кнопки переключения субтитров -->
            <slot name="subtitles">
                <SbsVideoPlayerSubtitles v-show="hasSubtitles" />
            </slot>

            <!-- @slot Слот для кнопки переключения звука -->
            <slot name="muteToggle">
                <SbsVideoPlayerMuteToggle />
            </slot>

            <!-- @slot Слот для регулировки громкости звука -->
            <slot name="volumeControl">
                <SbsVideoPlayerVolume />
            </slot>

            <!-- @slot Слот для кнопки переключения режима "картинка-в-картинке" -->
            <slot name="pictureInPicture">
                <SbsVideoPlayerPictureInPicture v-show="!isFirefox" />
            </slot>

            <!-- @slot Слот для кнопки переключения полноэкранного режима -->
            <slot name="fullscreen">
                <SbsVideoPlayerFullscreen />
            </slot>
        </template>

        <video
            v-once
            v-bind="videoData"
            :id="videoId"
            class="video-js"
            :class="videoClasses"
            @contextmenu.prevent="">

            <!-- @slot Слот по-умолчанию -->
            <slot></slot>
        </video>
        <SbsVideoPlayerStub v-if="isInPictureInPicture" />
    </div>
</template>

<script>
import { detect } from 'detect-browser';
import videojs from 'video.js';
import './videoEpisodes.js';
import videojsLocalization from 'video.js/dist/lang/ru.json';
import { AllColors } from '../../utils/colors.js';
import { getRandomId } from '../../utils/helpers.js';
import themesMixin from '../../mixins/themes.js';
import SbsVideoPlayerPlayToggle from './control-bar/SbsVideoPlayerPlayToggle.vue';
import SbsVideoPlayerTimeDisplay from './control-bar/SbsVideoPlayerTimeDisplay.vue';
import SbsVideoPlayerPlaybackRate from './control-bar/SbsVideoPlayerPlaybackRate.vue';
import SbsVideoPlayerLoadingSpinner from './control-bar/SbsVideoPlayerLoadingSpinner.vue';
import SbsVideoPlayerProgressControl from './control-bar/SbsVideoPlayerProgressControl.vue';
import SbsVideoPlayerSubtitles from './control-bar/SbsVideoPlayerSubtitles.vue';
import SbsVideoPlayerMuteToggle from './control-bar/SbsVideoPlayerMuteToggle.vue';
import SbsVideoPlayerVolume from './control-bar/SbsVideoPlayerVolume.vue';
import SbsVideoPlayerPictureInPicture from './control-bar/SbsVideoPlayerPictureInPicture.vue';
import SbsVideoPlayerFullscreen from './control-bar/SbsVideoPlayerFullscreen.vue';
import SbsVideoPlayerStub from './SbsVideoPlayerStub.vue';

videojs.addLanguage('ru', videojsLocalization);

const ICON_DISPLAY_TIME = 1000;
const REWIND_TIME = 15;

/**
 * Видеоплеер
 */
export default {
    name: 'SbsVideoPlayer',
    components: {
        SbsVideoPlayerStub,
        SbsVideoPlayerFullscreen,
        SbsVideoPlayerPictureInPicture,
        SbsVideoPlayerVolume,
        SbsVideoPlayerMuteToggle,
        SbsVideoPlayerSubtitles,
        SbsVideoPlayerProgressControl,
        SbsVideoPlayerLoadingSpinner,
        SbsVideoPlayerPlaybackRate,
        SbsVideoPlayerTimeDisplay,
        SbsVideoPlayerPlayToggle,
    },
    mixins: [themesMixin],
    emits: ['episodes:change', 'ready'],
    provide() {
        return {
            videoPlayerComponent: this,
        };
    },
    data() {
        return {
            player: null,
            playerContainer: null,
            isForwardSeeking: false,
            isBackwardSeeking: false,
            isWaiting: false,
            isInPictureInPicture: false,
            hasSubtitles: false,
            textTracks: null,
        };
    },
    computed: {
        videoId() {
            return this.id || `sbs-video-player-${getRandomId()}`;
        },
        videoContainerSelector() {
            return `#${this.videoId}`;
        },
        videoClasses() {
            return [this.videoClass];
        },
        videoData() {
            return {
                ...this.$attrs,
                'poster': this.poster,
                'preload': 'auto',
                'data-aspect': this.aspectRatio,
                'crossorigin': 'use-credentials',
            };
        },
        isFirefox() {
            const result = detect();

            return result?.name === 'firefox';
        },
    },
    methods: {
        initPlayer() {
            this.player.aspectRatio(this.aspectRatio);
        },
        handleKeydown(event) {
            if (this.isWaiting) {
                return;
            }

            if (event.key === 'ArrowLeft') {
                this.player.currentTime(this.player.currentTime() - REWIND_TIME);
                this.isBackwardSeeking = true;
                setTimeout(() => {
                    this.isBackwardSeeking = false;
                }, ICON_DISPLAY_TIME);
            } else if (event.key === 'ArrowRight') {
                this.player.currentTime(this.player.currentTime() + REWIND_TIME);
                this.isForwardSeeking = true;
                setTimeout(() => {
                    this.isForwardSeeking = false;
                }, ICON_DISPLAY_TIME);
            }
        },
        setVideoCssVarTheme(colorTheme) {
            if (!colorTheme) return;

            this.$el.style.setProperty(
                '--video-player-theme',
                `var(--${colorTheme})`,
            );
        },
        setPictureInPictureMode() {
            this.isInPictureInPicture = this.player.isInPictureInPicture();
        },
        disableWaiting() {
            this.isWaiting = false;
        },
        enableWaiting() {
            this.isWaiting = true;
        },
        setSubtitlesMode() {
            this.hasSubtitles = Boolean(this.player.textTracks().length);
        },
        handleEpisodeChange(event, { value }) {
            this.$emit('episodes:change', {
                event,
                value,
            });
        },
        handleReady(event) {
            this.$emit('ready', event);
        },
        addListeners() {
            this.player.on('waiting', this.enableWaiting);
            this.player.on('playing', this.disableWaiting);
            this.player.on(['enterpictureinpicture', 'leavepictureinpicture'], this.setPictureInPictureMode);
            this.player.on('texttrackchange', this.setSubtitlesMode);
            this.player.on('episodes:change', this.handleEpisodeChange);
            this.player.on('ready', this.handleReady);
            this.player.on('loadedmetadata', this.setSubtitlesMode); // fix subtitles for safari
        },
        removeListeners() {
            this.player.off('waiting', this.enableWaiting);
            this.player.off('playing', this.disableWaiting);
            this.player.off(['enterpictureinpicture', 'leavepictureinpicture'], this.setPictureInPictureMode);
            this.player.off('texttrackchange', this.setSubtitlesMode);
            this.player.off('episodes:change', this.handleEpisodeChange);
            this.player.off('ready', this.handleReady);
            this.player.off('loadedmetadata', this.setSubtitlesMode);
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Разрешение видео
         */
        aspectRatio: {
            type: String,
            default: '16:9', // требование бизнеса всегда видео 16 к 9
        },
        /**
         * Тема (цветовые решения)
         */
        colorTheme: {
            type: String,
            default: AllColors.SUCCESS,
        },
        /**
         * Уникальный идентификатор видео
         */
        id: {
            type: String,
            default: '',
        },
        /**
         * Обложка для видео
         */
        poster: {
            type: String,
            default: '',
        },
        /**
         * Путь до видеофайла
         */
        sources: {
            type: [Array, Object],
            default: () => [],
        },
        /**
         * Класс для видео
         */
        videoClass: {
            type: String,
            default: '',
        },
        /**
         * Массив эпизодов
         */
        episodes: {
            type: Array,
            default: () => ([]),
        },
    },
    watch: {
        isForwardSeeking(value) {
            if (value) {
                this.player.addClass('vjs-forward-seeking');
            } else {
                this.player.removeClass('vjs-forward-seeking');
            }
        },
        isBackwardSeeking(value) {
            if (value) {
                this.player.addClass('vjs-backward-seeking');
            } else {
                this.player.removeClass('vjs-backward-seeking');
            }
        },
        poster(value) {
            this.player.poster(value);
        },
        sources(current, previous) {
            if (current.src === previous.src) return;

            this.player?.pause();
            this.player?.hasStarted(false);
            this.player?.currentTime(0);
            this.player?.src(current);
        },
        episodes(value) {
            this.player?.episodes().reset(value);
        },
    },
    // eslint-disable-next-line max-lines-per-function
    mounted() {
        this.setVideoCssVarTheme(this.colorTheme);
        this.player = videojs(this.videoId, {
            sources: this.sources, // путь до видеофайла
            controls: true, // отображает панель управления видеоплеера
            autoplay: false, // автовоспроизведение при загрузке видео
            noUITitleAttributes: true, // скрывает все title атрибуты на панели управления
            playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2], // скорость воспроизведения видео
            language: 'ru', // языковая настройка проигрывателя
            loop: false, // не перезапускает видео, если оно закончилось
            inactivityTimeout: 0, // отключает скрытие control bar, когда плеер неактивен (курсор не на плеере)
            loadingSpinner: false, // отключение стандартного спиннера
            enableSourceset: true, // вспомогательная опция для динамического переключения src видео
            controlBar: {
                playToggle: false,
                playbackRateMenuButton: false,
                progressControl: false,
                currentTimeDisplay: false,
                timeDivider: false,
                durationDisplay: false,
                subsCapsButton: false,
                volumePanel: false,
                pictureInPictureToggle: false,
                fullscreenToggle: false,
                customControlSpacer: false,
                liveDisplay: false,
                seekToLive: false,
                remainingTimeDisplay: false,
                chaptersButton: false,
                descriptionsButton: false,
                subtitlesButton: false,
                audioTrackButton: false,
            },
            html5: {
                nativeTextTracks: false,
            },
        });
        this.playerContainer = this.player.el();
        this.addListeners();
        this.initPlayer();
        window.addEventListener('keydown', this.handleKeydown);

        if (this.episodes) {
            this.player.episodes({
                breakOverlay: {
                    display: true,
                    text(episode) {
                        return `<div class="vjs-overlay-name">Эпизод:</div><div class="vjs-overlay-title">${episode.text}</div>`;
                    },
                },
                episodes: this.episodes,
            });
        }
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeydown);
        this.removeListeners();
    },
};
</script>

<style lang="scss">
// stylelint-disable declaration-no-important
@import "~video.js/dist/video-js.css";

$control-bar-mb-10: 10px;

.video-js {
    font-family: "Fedra Sans Pro", Arial, sans-serif;
}

.vjs-poster {
    z-index: 1;
    background-size: cover;
}

.vjs-big-play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 2;
    width: 120px;
    height: 120px;
    background-color: rgba($black, 0.65);
    border: none;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition-duration: 0.4s;
    transition-property: border, outline, background-color;

    &:focus,
    .video-js &,
    .video-js:hover & {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 120px;
        height: 120px;
        background-color: rgba($black, 0.65);
        border: none;
        border-radius: 50%;
        transform: translate(-50%, -50%);
        transition-duration: 0.4s;
        transition-property: border, outline, background-color;
    }

    // размер кнопки биг плей при фулскрине
    .vjs-fullscreen &,
    .video-js:hover.vjs-fullscreen & {
        width: 150px;
        height: 150px;
    }

    // видимость кнопки
    .vjs-paused &,
    .vjs-ended &,
    .vjs-seeking &,
    .vjs-forward-seeking &,
    .vjs-backward-seeking & {
        display: block;
    }

    // отключает видимость кнопки при буферизации
    .vjs-waiting:not(.vjs-forward-seeking):not(.vjs-backward-seeking) & {
        display: none;
    }

    // скрытие кнопки при перетаскивании ползунка на полосе прогресса видео
    .vjs-has-started.vjs-paused.vjs-scrubbing & {
        display: none;
    }

    // скрытие кнопки плей в режиме "картинка в картинке"
    .vjs-picture-in-picture & {
        display: none;
    }

    // стилизация иконки кнопки плей
    .vjs-icon-placeholder::before {
        .video-js & {
            top: 50%;
            left: 50%;
            width: 56px;
            height: 56px;
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
            transform: translate(-50%, -50%);
            content: "";
        }

        // иконка плей на биг плей перед запуском видео
        .vjs-paused:not(.vjs-has-started) & {
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='56' height='56' viewBox='0 0 56 56' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M16.75 9.38048L16.75 46.6196L49 28L16.75 9.38048ZM16.75 5.91638C15.4167 5.14658 13.75 6.10883 13.75 7.64843L13.75 48.3516C13.75 49.8912 15.4167 50.8535 16.75 50.0837L52 29.7321C53.3333 28.9623 53.3333 27.0378 52 26.268L16.75 5.91638Z' fill='white'/%3e%3c/svg%3e");
        }

        // иконка на биг плей - пауза
        .vjs-paused.vjs-has-started & {
            background-image: url("../../assets/icons/svg/big-play-button.svg");
        }

        // иконка на биг плей - конец видео
        .vjs-paused.vjs-ended & {
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='56' height='56' viewBox='0 0 56 56' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M7.86288 7.8629L12.4757 12.4758C16.4676 8.48387 21.9675 6 27.9997 6C40.1528 6 50.0881 15.9355 49.9994 28.0887C49.9994 40.2419 40.1528 50 27.9997 50C22.4111 50 17.1773 47.871 13.2741 44.4113C12.8306 43.9677 12.8306 43.2581 13.2741 42.8145L15.0483 41.0403C15.4031 40.6855 16.0241 40.6855 16.4676 41.0403C19.5724 43.7903 23.5643 45.3871 27.9997 45.3871C37.6689 45.3871 45.3866 37.6694 45.3866 28C45.3866 18.4194 37.6689 10.6129 27.9997 10.6129C23.2094 10.6129 18.8627 12.5645 15.7579 15.7581L20.5482 20.5484C21.2579 21.2581 20.7256 22.3226 19.8385 22.3226H7.0645C6.53225 22.3226 6 21.879 6 21.2581V8.57258C6 7.68548 7.15321 7.15323 7.86288 7.8629Z' fill='white'/%3e%3c/svg%3e");
        }

        // иконка на биг плей - перемотка вперед
        .vjs-paused.vjs-has-started.vjs-forward-seeking &,
        .vjs-playing.vjs-has-started.vjs-forward-seeking & {
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='56' height='56' viewBox='0 0 56 56' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M27.9978 7C16.3976 7 6.99561 16.402 6.99561 28.0022C6.99561 39.6023 16.3976 49.0044 27.9978 49.0044C39.5979 49.0044 49 39.6023 49 28.0022V26.3668H46.2456V28.0022C46.2456 38.0811 38.0767 46.25 27.9978 46.25C17.9189 46.25 9.74999 38.0811 9.74999 28.0022C9.74999 17.9233 17.9189 9.75438 27.9978 9.75438C33.3461 9.75438 38.1578 12.0553 41.4959 15.7232L36.6748 20.5443H48.2405V8.97865L43.4452 13.7739C39.6092 9.61109 34.109 7 27.9978 7Z' fill='white'/%3e%3cpath d='M25.08 32.898V23L22 23.084V23.952H23.974V32.898H25.08Z' fill='white'/%3e%3cpath d='M33.0848 29.916C33.0848 28.11 31.9088 27.018 30.0748 27.018C29.6408 27.018 29.2628 27.018 28.8148 27.046V23.952H32.7348V23H27.7508V28.096C28.3108 28.04 28.8008 28.012 29.2908 27.998C29.4728 27.998 29.6688 27.984 29.8648 27.984C31.0548 27.984 31.9788 28.6 31.9788 30.084C31.9788 31.288 31.0688 32.002 29.8088 32.002C28.8568 32.002 28.2128 31.89 27.6808 31.75L27.4008 32.73C27.7648 32.842 28.5488 33.024 29.8088 33.024C31.7268 33.024 33.0848 31.946 33.0848 29.916Z' fill='white'/%3e%3c/svg%3e");
        }

        // иконка на биг плей - перемотка назад
        .vjs-paused.vjs-has-started.vjs-backward-seeking &,
        .vjs-playing.vjs-has-started.vjs-backward-seeking & {
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='56' height='56' viewBox='0 0 56 56' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M27.9978 7C39.598 7 49 16.402 49 28.0022C49 39.6023 39.598 49.0044 27.9978 49.0044C16.3977 49.0044 6.99563 39.6023 6.99563 28.0022V26.3668H9.75002V28.0022C9.75002 38.0811 17.9189 46.25 27.9978 46.25C38.0767 46.25 46.2456 38.0811 46.2456 28.0022C46.2456 17.9233 38.0767 9.75438 27.9978 9.75438C22.6495 9.75438 17.8378 12.0553 14.4997 15.7232L19.3208 20.5443H7.75514V8.97865L12.5504 13.7739C16.3864 9.61109 21.8866 7 27.9978 7Z' fill='white'/%3e%3cpath d='M25.9906 32.898V23L22.9106 23.084V23.952H24.8846V32.898H25.9906Z' fill='white'/%3e%3cpath d='M33.9955 29.916C33.9955 28.11 32.8195 27.018 30.9855 27.018C30.5515 27.018 30.1735 27.018 29.7255 27.046V23.952H33.6455V23H28.6615V28.096C29.2215 28.04 29.7115 28.012 30.2015 27.998C30.3835 27.998 30.5795 27.984 30.7755 27.984C31.9655 27.984 32.8895 28.6 32.8895 30.084C32.8895 31.288 31.9795 32.002 30.7195 32.002C29.7675 32.002 29.1235 31.89 28.5915 31.75L28.3115 32.73C28.6755 32.842 29.4595 33.024 30.7195 33.024C32.6375 33.024 33.9955 31.946 33.9955 29.916Z' fill='white'/%3e%3c/svg%3e");
        }

        // размер иконки биг плей при фуллскрине
        .vjs-fullscreen & {
            width: 78px;
            height: 78px;
        }
    }

    // стилизация текста под кнопкой плей, когда видео закончилось
    &::after {
        position: absolute;
        bottom: -37px;
        left: 50%;
        display: none;
        width: 100%;
        transform: translateX(-50%);
        content: "Повторить";

        @include typo-subprimary-medium;

        // видимость текста под кнопкой плей, когда видео закончилось
        .vjs-paused.vjs-ended & {
            display: block;
        }
    }
}

.vjs-text-track-display {
    bottom: 0;

    // отключение показа субтитров, когда еще не воспроизводится видео
    .video-js:not(.vjs-has-started) & {
        display: none;
    }

    & > div {
        margin: 10px 1.5% !important;
    }

    // стилизация субтитров
    .vjs-text-track-cue {
        inset: auto !important;
        bottom: 45px !important;
        width: 100% !important;
        height: auto !important;
        font-size: 28px !important;

        & > div {
            display: inline-block;
            padding: 8px 15px;
            font-family: "Fedra Sans Pro", Arial, sans-serif !important;
            line-height: calc(35 / 14) !important;
            border-radius: $rounded-10;
            box-decoration-break: clone;
            //eslint-disable-next-line declaration-block-no-duplicate-properties
            -webkit-box-decoration-break: clone; //stylelint-disable-line

            @include typo-secondary-demi;
        }
    }
}

.vjs-control-bar {
    .video-js & {
        justify-content: space-between;
        margin-bottom: $control-bar-mb-10;
        padding: 0 20px;
        color: $white;
        font-size: 14px;
        font-family: "Fedra Sans Pro", sans-serif;
        background-color: $transparent;
        transition: transform $trs;
    }

    // ширина всех кнопок панели контрол бара с учётом отступов макета
    .vjs-control {
        width: 40px;

        .vjs-icon-placeholder:hover::before {
            color: var(--video-player-theme);
            transition: color $trs-slow;
        }

        &:focus::before,
        &:hover::before,
        &:focus {
            text-shadow: none;
        }
    }

    // размер иконок в control bar
    .vjs-button > .vjs-icon-placeholder::before {
        font-size: 22px;
        line-height: 1.9;
    }

    // скрытие стандартного тугла плей
    .vjs-play-control .vjs-icon-placeholder::before {
        content: "";
    }

    .vjs-control.vjs-remaining-time {
        display: none;
    }

    .vjs-picture-in-picture & {
        z-index: 1;
    }
}

.sbs-video-player {
    --video-player-theme: #{$success}; // переменная для темы видеоплеера

    position: relative;
    width: 100%;

    .video-js {
        width: 100%;
    }

    .vjs-progress-control:hover .vjs-progress-holder {
        font-size: 14px;
    }

    // показ ползунка при ховере прогресса
    .vjs-progress-control:hover .vjs-play-progress::before {
        display: block;
    }
}

.vjs-episode {
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 100;
    width: 2px;
    height: 100%;
    background-color: #404040;
    opacity: 1;
    transition: opacity 0.2s ease;
}

.vjs-overlay {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 0;
    width: 182px;
    padding: 10px;
    background: #000000;
    border-radius: 10px;
    visibility: hidden;
    opacity: 0.5;

    &:hover {
        cursor: pointer;
        opacity: 0.9;
    }

    .vjs-overlay-content {
        .vjs-overlay-name {
            display: block;
            margin-bottom: 2px;
            font-weight: 500;
            font-size: 9px;
            line-height: 15px;
            text-transform: uppercase;
            opacity: 0.5;
        }

        .vjs-overlay-title {
            @include typo-subsecondary-book;
            @include text-clamp;
        }
    }
}

.vjs-time-tooltip {
    max-width: 189px;
    padding: 10px 12px;
    opacity: 0.9;

    @include typo-additional-book;

    .vjs-tooltip-title {
        @include text-clamp;
    }
}

</style>
